import i18nFactory, { i18nRaw as i18nRawFactory } from '@yandex-int/i18n';
import classNames from 'classnames';
import { memoize, reduce } from 'lodash';
import * as React from 'react';

import { mapRateCorrectionsToEditorRateCorrections } from '../../mappers/rateCorrections/rateCorrections';
import { Bem } from '../../service/Bem/Bem';
import { getErrorTextFromCountErrors } from '../../service/ErrorHelpers/ErrorHelpers';
import {
    getHintDescriptionForWeatherRateCorrectionItem,
    getWeatherChooserData
} from '../../service/RateCorrectionsEditorHelpers/RateCorrectionsEditorHelpers';
import { TestBem } from '../../service/TestBem';
import { UrlHelpers } from '../../service/UrlHelpers';
import { LinkTarget, Tld, ValidationResult } from '../../typings/common';
import { HelpLink } from '../../typings/doc-center';
import {
    DemographyRateCorrectionCondition,
    DeviceRateCorrectionCondition,
    DeviceRateCorrectionType,
    EditorRateCorrection,
    EditorRateCorrectionCondition,
    RateControlValue,
    RateCorrection,
    RateCorrectionAge,
    RateCorrectionConditionChooserItemsGroup,
    RateCorrectionConditionId,
    RateCorrectionGender,
    RateCorrectionMode,
    RateCorrectionType,
    RatesCorrectionsEditorValue,
    RatesCorrectionsValue,
    RetargetingConditionDataForRateCorrections,
    RetargetingRateCorrectionCondition,
    WeatherRateCorrectionCondition
} from '../../typings/ratesCorrections';
import { Alert, AlertSize, AlertType } from '../Alert/Alert';
import { ButtonDecorator } from '../ButtonDecorator/ButtonDecorator';
import { ExpandedEditBlock } from '../ExpandedEditBlock/ExpandedEditBlock';
import { RateCorrectionValue } from '../RateCorrectionValue/RateCorrectionValue';
import {
    onChangeConditionParams,
    RatesCorrectionsList
} from '../RatesCorrectionsList/RatesCorrectionsList';
import { Shape2Icon } from '../Shape2/Shape2';
import { StyledLink, StyledLinkTheme } from '../StyledLink/StyledLink';
import { Tab, TabList, TabPanel, Tabs, TabsSize } from '../Tabs/Tabs';

import './RateCorrectionsEditor.css';
import * as keyset from './RateCorrectionsEditor.i18n';

const i18n = i18nFactory(keyset);
const i18nRaw = i18nRawFactory(keyset);

const blockClass = 'rate-corrections-editor';

const tabNames: Record<RateCorrectionType, string> = {
    [RateCorrectionType.retargeting]: i18n('retargeting-tab'),
    [RateCorrectionType.device]: i18n('device-tab'),
    [RateCorrectionType.demography]: i18n('demography-tab'),
    [RateCorrectionType.videoAddition]: i18n('video-addition-tab'),
    [RateCorrectionType.smartBanners]: i18n('smart-banners-tab'),
    [RateCorrectionType.weather]: i18n('weather-tab')
};

export const tabs = [
    RateCorrectionType.retargeting,
    RateCorrectionType.device,
    RateCorrectionType.demography,
    RateCorrectionType.videoAddition,
    RateCorrectionType.smartBanners,
    RateCorrectionType.weather
];

export type RateCorrectionsEditorProps = {
    cls?: string;
    tld: Tld;
    isOpen: boolean;
    currentTabType?: RateCorrectionType;
    value: RatesCorrectionsValue;
    editorValue: RatesCorrectionsEditorValue;
    retargetingConditionsData: Array<
        RetargetingConditionDataForRateCorrections
    >;
    enabledDevices: Array<DeviceRateCorrectionType>;
    validation?: ValidationResult;
    onOpenClick: (value: RatesCorrectionsEditorValue) => void;
    onChangeCurrentTab: (value: RateCorrectionType) => void;
    onSave: () => void;
    onCancel: () => void;
    onAddConditions: (type: RateCorrectionType, values: Array<string>) => void;
    onDeleteCondition: (
        type: RateCorrectionType,
        conditionId: RateCorrectionConditionId
    ) => void;
    onToggleRateAvailability: (type: RateCorrectionType) => void;
    onChangeMassRateControl: (
        type: RateCorrectionType,
        value: RateControlValue
    ) => void;
    onChangeSingleMassRateControl: (
        type: RateCorrectionType,
        value: RateControlValue
    ) => void;
    onChangeCondition: (params: onChangeConditionParams) => void;
    onOpenHelp: (helpTooltipId: HelpLink) => void;
    onChangeMode: (type: RateCorrectionType, value: RateCorrectionMode) => void;
};

export class RateCorrectionsEditor extends React.PureComponent<
    RateCorrectionsEditorProps
> {
    render() {
        const { isOpen, editorValue } = this.props;
        const errorsLength = getErrorsLength(editorValue);

        return (
            <div
                className={classNames(
                    blockClass,
                    this.props.cls,
                    TestBem.cls({
                        block: blockClass,
                        mods: {
                            state: isOpen ? 'open' : 'closed'
                        }
                    })
                )}
            >
                <ExpandedEditBlock
                    header={this.getHeader()}
                    isOpen={isOpen}
                    onOpenClick={this.openPanel}
                    showOpenControl={true}
                    showCloseControl={false}
                    iconType={Shape2Icon.verticalArrow}
                    footer={
                        !isOpen && errorsLength > 0 ? (
                            <div
                                className={Bem.cls(
                                    blockClass,
                                    'validation-error'
                                )}
                            >
                                <Alert
                                    type={AlertType.error}
                                    size={AlertSize.s}
                                >
                                    {getErrorTextFromCountErrors(errorsLength)}
                                </Alert>
                            </div>
                        ) : (
                            undefined
                        )
                    }
                >
                    {isOpen
                        ? this.getOpenedPanelContent()
                        : this.getClosedPanelContent()}
                </ExpandedEditBlock>
            </div>
        );
    }

    private openPanel = () => {
        const { onOpenClick, value } = this.props;

        onOpenClick(mapRateCorrectionsToEditorRateCorrections(value));
    };

    private getHeader = (): string | JSX.Element => {
        return (
            <h3
                className={classNames(
                    Bem.cls(blockClass, 'title'),
                    TestBem.cls(blockClass, 'title')
                )}
            >
                {i18n('title')}
            </h3>
        );
    };

    private getOpenedPanelContent = () => {
        const { currentTabType, editorValue, tld } = this.props;
        const relevantTabsTypes = getRelevantTabsTypes(editorValue);
        let currentTabIndex;

        if (currentTabType) {
            currentTabIndex = relevantTabsTypes.indexOf(currentTabType);
        } else {
            currentTabIndex = 0;
        }

        return (
            <>
                <p className={Bem.cls(blockClass, 'par')}>
                    {i18nRaw('opened-panel-description', {
                        moreInfoLink: (
                            <StyledLink
                                theme={StyledLinkTheme.blue}
                                href={UrlHelpers.getCommonHelpUrl(
                                    'direct/impressions/bids-adjustment.html',
                                    tld
                                )}
                                target={LinkTarget.blank}
                            >
                                {i18n('more-info-link')}
                            </StyledLink>
                        )
                    })}
                </p>

                <Tabs
                    size={TabsSize.s}
                    selectedIndex={currentTabIndex}
                    onSelect={this.onTabChange}
                >
                    <TabList>
                        {relevantTabsTypes.map(tab => (
                            <Tab key={tab}>
                                <span
                                    className={TestBem.cls(blockClass, 'tab', {
                                        type: tab
                                    })}
                                >
                                    {tabNames[tab]}
                                </span>
                                {editorValue[tab].hasChanges ? (
                                    <span
                                        className={Bem.cls(
                                            blockClass,
                                            'tab-changes-sign'
                                        )}
                                    />
                                ) : null}
                            </Tab>
                        ))}
                    </TabList>

                    {relevantTabsTypes.map(tab => (
                        <TabPanel key={tab}>
                            {this.getRatesCorrectionList(tab)}
                        </TabPanel>
                    ))}
                </Tabs>

                {this.getFooter()}
            </>
        );
    };

    private getClosedPanelContent = () => {
        const { value } = this.props;

        const correctionsWithConditions = Object.keys(value).reduce(
            (
                res: Array<RateCorrection>,
                correctionType: RateCorrectionType
            ) => {
                const correction = value[correctionType];
                const { items, isSwitchedOn } = correction;

                if (isSwitchedOn !== false && items.length > 0) {
                    res.push(correction);
                }

                return res;
            },
            []
        );

        if (correctionsWithConditions.length > 0) {
            return this.getValueHint(correctionsWithConditions);
        }

        return (
            <>
                <p className={Bem.cls(blockClass, 'value')}>
                    {i18n('no-value')}
                </p>

                <p className={Bem.cls(blockClass, 'hint')}>
                    {i18n('closed-panel-description')}
                </p>
            </>
        );
    };

    private getValueHint = (
        correctionsWithConditions: Array<RateCorrection>
    ) => {
        if (correctionsWithConditions.length === 0) {
            return null;
        }

        return (
            <ul className={Bem.cls(blockClass, 'value-list')}>
                {correctionsWithConditions.map(correction => {
                    const { type } = correction;

                    return (
                        <li
                            key={type}
                            className={Bem.cls(blockClass, 'value-list-item')}
                        >
                            <span
                                className={Bem.cls(
                                    blockClass,
                                    'value-list-title'
                                )}
                            >
                                {tabNames[type]}
                            </span>

                            <span
                                className={Bem.cls(
                                    blockClass,
                                    'value-list-value'
                                )}
                            >
                                {this.getValueHintItemValue(correction)}
                            </span>

                            {correction.mode === RateCorrectionMode.allDone &&
                                correction.singleMassRateControlValue !==
                                    undefined && (
                                    <RateCorrectionValue
                                        value={
                                            correction.singleMassRateControlValue
                                        }
                                    />
                                )}
                        </li>
                    );
                })}
            </ul>
        );
    };

    private getValueHintItemValue = (correction: RateCorrection) => {
        const { retargetingConditionsData } = this.props;

        switch (correction.type) {
            case RateCorrectionType.demography: {
                return correction.items.map(
                    (item: DemographyRateCorrectionCondition) => {
                        return (
                            <span
                                key={item.id}
                                className={Bem.cls(
                                    blockClass,
                                    'value-list-condition'
                                )}
                            >
                                {`${rateCorrectionGenderToText[item.gender]} ${
                                    rateCorrectionAgeToText[item.age]
                                } `}
                                <RateCorrectionValue value={item.value} />
                            </span>
                        );
                    }
                );
            }

            case RateCorrectionType.retargeting: {
                return correction.items.map(
                    (item: RetargetingRateCorrectionCondition) => {
                        const retargetingItem = retargetingConditionsData.find(
                            ({ id }) => id === item.retargetingConditionId
                        );

                        return (
                            <span
                                key={item.id}
                                className={classNames(
                                    Bem.cls(blockClass, 'value-list-condition'),
                                    TestBem.cls(
                                        blockClass,
                                        'value-list-condition'
                                    )
                                )}
                            >
                                {retargetingItem ? retargetingItem.name : ''}{' '}
                                <RateCorrectionValue value={item.value} />
                            </span>
                        );
                    }
                );
            }

            case RateCorrectionType.device: {
                return correction.items.map(
                    (item: DeviceRateCorrectionCondition) => {
                        return (
                            <span
                                key={item.id}
                                className={Bem.cls(
                                    blockClass,
                                    'value-list-condition'
                                )}
                            >
                                {
                                    mobileRateCorrectionTypeToText[
                                        item.deviceType
                                    ]
                                }{' '}
                                <RateCorrectionValue value={item.value} />
                            </span>
                        );
                    }
                );
            }

            case RateCorrectionType.videoAddition:
            case RateCorrectionType.smartBanners: {
                return (
                    <span
                        className={Bem.cls(blockClass, 'value-list-condition')}
                    >
                        {i18n('all-items')}{' '}
                        <RateCorrectionValue
                            value={correction.items[0].value}
                        />
                    </span>
                );
            }

            case RateCorrectionType.weather: {
                return correction.items.map(
                    (item: WeatherRateCorrectionCondition) => {
                        return (
                            <span
                                key={item.id}
                                className={Bem.cls(
                                    blockClass,
                                    'value-list-condition'
                                )}
                            >
                                {getHintDescriptionForWeatherRateCorrectionItem(
                                    item
                                )}{' '}
                                {item.value !== undefined && (
                                    <RateCorrectionValue value={item.value} />
                                )}
                            </span>
                        );
                    }
                );
            }

            default:
                throw Error('Неподдерживанемый тип корректировки ставки');
        }
    };

    private getFooter = () => {
        const { onSave, onCancel, validation } = this.props;
        const isSavingDisabled = validation && validation.valid === false;

        return (
            <div className={Bem.cls(blockClass, 'footer')}>
                <ButtonDecorator
                    cls={classNames(
                        Bem.cls(blockClass, 'footer-btn'),
                        TestBem.cls(blockClass, 'save-btn')
                    )}
                    view={'classic'}
                    theme={'normal'}
                    onClick={onSave}
                    size={'n'}
                    disabled={isSavingDisabled}
                >
                    {i18n('ready')}
                </ButtonDecorator>

                <ButtonDecorator
                    cls={Bem.cls(blockClass, 'footer-btn')}
                    view={'classic'}
                    theme={'normal'}
                    onClick={onCancel}
                    size={'n'}
                >
                    {i18n('cancel')}
                </ButtonDecorator>
            </div>
        );
    };

    private onTabChange = (index: number) => {
        const { editorValue } = this.props;
        const relevantTabsTypes = getRelevantTabsTypes(editorValue);
        this.props.onChangeCurrentTab(relevantTabsTypes[index]);
    };

    private getRatesCorrectionList = (tabType: RateCorrectionType) => {
        const {
            retargetingConditionsData,
            enabledDevices,
            editorValue,
            onAddConditions,
            onDeleteCondition,
            onChangeCondition,
            onChangeMassRateControl,
            onToggleRateAvailability,
            onOpenHelp,
            onChangeMode,
            onChangeSingleMassRateControl
        } = this.props;

        const tabValue = editorValue[tabType];
        const listBaseParams = {
            cls: Bem.cls(blockClass, 'control'),
            isSwitchedOn: tabValue.isSwitchedOn,
            hasChanges: tabValue.hasChanges,
            errors: tabValue.errors,
            maxRate: tabValue.maxRate,
            minRate: tabValue.minRate,
            minTemperatureLimit: tabValue.minTemperatureLimit,
            maxTemperatureLimit: tabValue.maxTemperatureLimit,
            maxConditionsNumber: tabValue.maxConditionsNumber,
            massRateControlValue: tabValue.massRateControlValue,
            mode: tabValue.mode,
            singleMassRateControlValue: tabValue.singleMassRateControlValue,
            errorsSingleMassRateControl: tabValue.errorsSingleMassRateControl,
            onAddConditions: onAddConditions,
            onDeleteCondition: onDeleteCondition,
            onToggleRateAvailability: onToggleRateAvailability,
            onChangeMassRateControl: onChangeMassRateControl,
            onChangeCondition: onChangeCondition,
            onOpenHelp: onOpenHelp,
            onChangeMode: onChangeMode,
            onChangeSingleMassRateControl: onChangeSingleMassRateControl
        };

        switch (tabValue.type) {
            case RateCorrectionType.retargeting:
                return (
                    <RatesCorrectionsList
                        {...listBaseParams}
                        type={tabValue.type}
                        items={tabValue.items}
                        chooserData={[
                            {
                                hasMultiChoice: true,
                                items: retargetingConditionsData
                            }
                        ]}
                    />
                );

            case RateCorrectionType.device:
                return (
                    <RatesCorrectionsList
                        {...listBaseParams}
                        type={tabValue.type}
                        items={tabValue.items}
                        chooserData={getDevicesChooserData(enabledDevices)}
                    />
                );

            case RateCorrectionType.demography:
                return (
                    <RatesCorrectionsList
                        {...listBaseParams}
                        type={tabValue.type}
                        items={tabValue.items}
                    />
                );

            case RateCorrectionType.videoAddition:
                return (
                    <RatesCorrectionsList
                        {...listBaseParams}
                        type={tabValue.type}
                        items={tabValue.items}
                    />
                );

            case RateCorrectionType.smartBanners:
                return (
                    <RatesCorrectionsList
                        {...listBaseParams}
                        type={tabValue.type}
                        items={tabValue.items}
                    />
                );

            case RateCorrectionType.weather:
                return (
                    <RatesCorrectionsList
                        {...listBaseParams}
                        type={tabValue.type}
                        items={tabValue.items}
                        chooserData={weatherChooserData}
                    />
                );

            default:
                throw Error('Неподдерживаемый тип корректировки ставок');
        }
    };
}

const getErrorsLength = memoize(
    (editorValue: RatesCorrectionsEditorValue): number => {
        return reduce(
            editorValue,
            (errorsLength: number, tabValue: EditorRateCorrection) => {
                const tabItems: (EditorRateCorrectionCondition)[] =
                    tabValue.items;

                errorsLength += reduce(
                    tabItems,
                    (
                        itemsErrors: number,
                        item: EditorRateCorrectionCondition
                    ) => {
                        itemsErrors += reduce(
                            item.errors,
                            (res, itemError) => {
                                if (itemError && itemError.length !== 0) {
                                    res++;
                                }

                                return res;
                            },
                            0
                        );

                        return itemsErrors;
                    },
                    0
                );

                return errorsLength;
            },
            0
        );
    }
);

const getDevicesChooserData = memoize(
    (
        enabledDevices: Array<DeviceRateCorrectionType>
    ): Array<RateCorrectionConditionChooserItemsGroup> => {
        const devicesChooserData = [
            {
                name: i18n('mobile'),
                hasMultiChoice: false,
                items: [
                    DeviceRateCorrectionType.allMobile,
                    DeviceRateCorrectionType.ios,
                    DeviceRateCorrectionType.android
                ].map(id => {
                    return {
                        id: id,
                        name: mobileRateCorrectionTypeToText[id]
                    };
                })
            },
            {
                hasMultiChoice: true,
                items: [DeviceRateCorrectionType.desktops].map(id => {
                    return {
                        id: id,
                        name: mobileRateCorrectionTypeToText[id]
                    };
                })
            }
        ];

        return devicesChooserData.reduce(
            (
                res: Array<RateCorrectionConditionChooserItemsGroup>,
                devicesGroup: RateCorrectionConditionChooserItemsGroup
            ) => {
                const enabledGroupItems = devicesGroup.items.filter(
                    devicesGroupItem =>
                        enabledDevices.includes(
                            devicesGroupItem.id as DeviceRateCorrectionType
                        )
                );

                if (enabledGroupItems.length === 0) {
                    return res;
                }

                res.push({
                    ...devicesGroup,
                    items: enabledGroupItems
                });

                return res;
            },
            []
        );
    }
);

const getRelevantTabsTypes = memoize(
    (editorValue: RatesCorrectionsEditorValue) =>
        tabs.filter(tab => Boolean(editorValue[tab]))
);

const weatherChooserData = [
    {
        hasMultiChoice: true,
        items: getWeatherChooserData()
    }
];

export const rateCorrectionAgeToText: Record<RateCorrectionAge, string> = {
    [RateCorrectionAge.all]: i18n('all-ages'),
    [RateCorrectionAge.age0_17]: i18n('0-17'),
    [RateCorrectionAge.age18_24]: i18n('18-24'),
    [RateCorrectionAge.age25_34]: i18n('25-34'),
    [RateCorrectionAge.age35_44]: i18n('35-44'),
    [RateCorrectionAge.age45_54]: i18n('45-54'),
    [RateCorrectionAge.age55_]: i18n('55-')
};

export const rateCorrectionGenderToText: Record<
    RateCorrectionGender,
    string
> = {
    [RateCorrectionGender.all]: i18n('all-genders'),
    [RateCorrectionGender.male]: i18n('male'),
    [RateCorrectionGender.female]: i18n('female')
};

export const mobileRateCorrectionTypeToText: Record<
    DeviceRateCorrectionType,
    string
> = {
    [DeviceRateCorrectionType.allMobile]: i18n('all-mobile'),
    [DeviceRateCorrectionType.ios]: i18n('ios'),
    [DeviceRateCorrectionType.android]: i18n('android'),
    [DeviceRateCorrectionType.desktops]: i18n('desktops')
};
